/*
 * Calculadora de gastos generales — Electricidad.
 * Introduces el consumo (por potencia+horas, kWh/día o kWh/mes), el precio del kWh
 * (o pídelo a la IA), la merma % y los impuestos, y obtienes el coste anual/mensual.
 * Puedes guardar el cálculo asociado a un site para tener histórico.
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../api/client';
import { useSyncListener } from '../hooks/useDataSync';
import { Zap, Sparkles, Save, Trash2, Loader2, Euro, Calculator, BarChart3, Percent } from 'lucide-react';

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmt = (n, d = 2) => new Intl.NumberFormat('es-ES', { maximumFractionDigits: d }).format(n || 0);

const MODES = [
  { key: 'potencia_horas', label: 'Potencia + horas' },
  { key: 'kwh_dia',        label: 'kWh / día' },
  { key: 'kwh_mes',        label: 'kWh / mes' },
];

const NumberInput = ({ label, value, onChange, suffix, step = '0.01', min = 0, dataTestId }) => (
  <label className="block">
    <span className="text-white/60 text-xs uppercase tracking-wide">{label}</span>
    <div className="mt-1 flex items-center bg-black border border-white/10 rounded-xl focus-within:border-[#A3FF12]">
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)}
        step={step} min={min} data-testid={dataTestId}
        className="w-full bg-transparent text-white px-3 py-2.5 outline-none text-lg font-bold"/>
      {suffix && <span className="text-white/40 text-xs px-3">{suffix}</span>}
    </div>
  </label>
);

const GastosElectricos = () => {
  const [sites, setSites] = useState([]);
  const [saved, setSaved] = useState([]);
  const [mode, setMode] = useState('potencia_horas');
  const [potenciaKw, setPotenciaKw] = useState('3.5');
  const [horasDia, setHorasDia] = useState('24');
  const [kwh, setKwh] = useState('84');
  const [priceKwh, setPriceKwh] = useState('0.15');
  const [mermaPct, setMermaPct] = useState('25');
  const [ivaPct, setIvaPct] = useState('21');
  const [impElectPct, setImpElectPct] = useState('5.11');
  const [siteId, setSiteId] = useState('');
  const [notes, setNotes] = useState('');
  const [aiInfo, setAiInfo] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadSites = useCallback(async () => {
    try {
      const { data } = await api.get('/sites/lite');
      setSites(data || []);
    } catch { /* ignore */ }
  }, []);
  const loadSaved = useCallback(async () => {
    try {
      const { data } = await api.get('/gastos/electricos');
      setSaved(data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadSites(); loadSaved(); }, [loadSites, loadSaved]);
  useSyncListener(loadSaved, ['gastos_electricos']);

  // ---- cálculo local (idéntica lógica al backend) ----
  const result = useMemo(() => {
    const n = (v) => (v === '' || v == null ? 0 : Number(v));
    const price = n(priceKwh);
    const merma = n(mermaPct);
    const iva = n(ivaPct);
    const impE = n(impElectPct);
    let base = 0;
    if (mode === 'potencia_horas') base = n(potenciaKw) * n(horasDia) * 365;
    if (mode === 'kwh_dia')        base = n(kwh) * 365;
    if (mode === 'kwh_mes')        base = n(kwh) * 12;
    // Energía no consumida se DESCUENTA del consumo base
    const conMerma = base * Math.max(0, 1 - merma / 100);
    const costeEnergia = conMerma * price;
    const impElectVal = costeEnergia * impE / 100;
    const subtotal = costeEnergia + impElectVal;
    const ivaVal = subtotal * iva / 100;
    const total = subtotal + ivaVal;
    return {
      base, conMerma, price, costeEnergia, impElectVal, subtotal, ivaVal, total,
      mensual: total / 12,
    };
  }, [mode, potenciaKw, horasDia, kwh, priceKwh, mermaPct, ivaPct, impElectPct]);

  const askAi = async () => {
    setAiLoading(true); setAiInfo(null);
    try {
      const { data } = await api.post('/gastos/precio-kwh-ai');
      setAiInfo(data);
      setPriceKwh(String(data.price_eur_kwh));
    } catch (e) {
      alert('No se ha podido consultar el precio del kWh: ' + (e.response?.data?.detail || e.message));
    } finally { setAiLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        mode,
        potencia_kw: mode === 'potencia_horas' ? Number(potenciaKw) : null,
        horas_dia:   mode === 'potencia_horas' ? Number(horasDia) : null,
        kwh:         mode === 'potencia_horas' ? null : Number(kwh),
        price_eur_kwh: Number(priceKwh),
        merma_pct: Number(mermaPct),
        iva_pct: Number(ivaPct),
        impuesto_electrico_pct: Number(impElectPct),
        site_id: siteId || null,
        site_name: sites.find((s) => s.id === siteId)?.name || '',
        site_code: sites.find((s) => s.id === siteId)?.code || '',
        notes: notes || '',
      };
      await api.post('/gastos/electricos', payload);
      await loadSaved();
      setNotes('');
    } catch (e) {
      alert('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!confirm('¿Eliminar este cálculo?')) return;
    await api.delete(`/gastos/electricos/${id}`);
    await loadSaved();
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto space-y-6" data-testid="gastos-electricos-page">
      <div>
        <div className="text-xs font-bold tracking-widest text-[#A3FF12]">GASTOS GENERALES</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Calculadora eléctrica</h1>
        <p className="text-white/50 text-sm mt-1">Calcula el coste anual de electricidad por site con precio Adquiriam, merma y todos los impuestos.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ---- INPUT ---- */}
        <div className="lg:col-span-2 bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6 space-y-5">
          {/* Modo */}
          <div>
            <label className="text-white/60 text-xs uppercase tracking-wide">Modo de entrada</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {MODES.map((m) => (
                <button key={m.key} onClick={() => setMode(m.key)} data-testid={`mode-${m.key}`}
                  className={`px-4 py-2 rounded-full text-sm font-bold border transition-colors ${
                    mode === m.key
                      ? 'bg-[#A3FF12]/10 border-[#A3FF12]/40 text-[#A3FF12]'
                      : 'bg-white/5 border-white/10 text-white/60 hover:text-white'
                  }`}>{m.label}</button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {mode === 'potencia_horas' && (
              <>
                <NumberInput label="Potencia contratada" suffix="kW" value={potenciaKw} onChange={setPotenciaKw} dataTestId="input-potencia"/>
                <NumberInput label="Horas al día encendido" suffix="h" value={horasDia} onChange={setHorasDia} dataTestId="input-horas"/>
              </>
            )}
            {mode === 'kwh_dia' && <NumberInput label="Consumo diario" suffix="kWh/día" value={kwh} onChange={setKwh} dataTestId="input-kwh-dia"/>}
            {mode === 'kwh_mes' && <NumberInput label="Consumo mensual" suffix="kWh/mes" value={kwh} onChange={setKwh} dataTestId="input-kwh-mes"/>}
          </div>

          {/* Precio kWh */}
          <div>
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <NumberInput label="Precio kWh (€ sin impuestos)" suffix="€/kWh" step="0.0001" value={priceKwh} onChange={setPriceKwh} dataTestId="input-price"/>
              </div>
              <button onClick={askAi} disabled={aiLoading} data-testid="btn-price-ai"
                className="flex items-center gap-2 bg-[#A3FF12]/10 border border-[#A3FF12]/30 text-[#A3FF12] hover:bg-[#A3FF12]/20 px-4 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50">
                {aiLoading ? <Loader2 className="animate-spin" size={16}/> : <Sparkles size={16}/>}
                {aiLoading ? 'Consultando…' : 'Sugerir con Adquiriam'}
              </button>
            </div>
            {aiInfo && (
              <div className="mt-2 text-xs text-white/50 bg-black/60 border border-white/5 rounded-lg px-3 py-2" data-testid="ai-price-info">
                <b className="text-[#A3FF12]">{aiInfo.price_eur_kwh} €/kWh</b> · {aiInfo.date} · {aiInfo.source}
                {aiInfo.notes && <div className="text-white/40 mt-1">{aiInfo.notes}</div>}
              </div>
            )}
          </div>

          {/* Merma + Impuestos */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <NumberInput label="Energía no consumida" suffix="%" value={mermaPct} onChange={setMermaPct} dataTestId="input-merma"/>
            <NumberInput label="Impuesto eléctrico" suffix="%" value={impElectPct} onChange={setImpElectPct} dataTestId="input-impelect"/>
            <NumberInput label="IVA" suffix="%" value={ivaPct} onChange={setIvaPct} dataTestId="input-iva"/>
          </div>

          {/* Site + notas */}
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-white/60 text-xs uppercase tracking-wide">Site (opcional)</span>
              <select value={siteId} onChange={(e) => setSiteId(e.target.value)} data-testid="input-site"
                className="mt-1 w-full bg-black border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm">
                <option value="">— Sin asignar —</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name} {s.code ? `(${s.code})` : ''}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-white/60 text-xs uppercase tracking-wide">Notas</span>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="ej. Cálculo 2026 con torre reforma"
                data-testid="input-notes"
                className="mt-1 w-full bg-black border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#A3FF12]"/>
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button onClick={save} disabled={saving || !Number(priceKwh)} data-testid="btn-save"
              className="flex items-center gap-2 bg-[#A3FF12] text-black font-black px-5 py-2.5 rounded-xl hover:bg-[#b8ff3a] disabled:opacity-40 text-sm">
              {saving ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Guardar cálculo
            </button>
            <span className="text-white/40 text-xs">Se guarda en el histórico con la fecha actual.</span>
          </div>
        </div>

        {/* ---- RESULT ---- */}
        <div className="bg-gradient-to-br from-[#A3FF12]/10 to-black border border-[#A3FF12]/30 rounded-2xl p-6 space-y-4" data-testid="result-card">
          <div className="flex items-center gap-2">
            <Calculator className="text-[#A3FF12]" size={18}/>
            <h2 className="text-white font-black text-lg">Coste eléctrico</h2>
          </div>
          <div>
            <div className="text-white/50 text-xs uppercase tracking-wide">Total anual</div>
            <div className="text-4xl sm:text-5xl font-black text-[#A3FF12]" data-testid="result-total-anual">{eur(result.total)}</div>
            <div className="text-white/60 text-sm mt-1">≈ <b>{eur(result.mensual)}</b> / mes</div>
          </div>

          <div className="border-t border-white/10 pt-3 space-y-1.5 text-sm">
            <Row label="kWh base anual" value={`${fmt(result.base, 0)} kWh`}/>
            <Row label={`kWh facturables (−${mermaPct}% no consumida)`} value={`${fmt(result.conMerma, 0)} kWh`}/>
            <Row label={`Coste energía (${fmt(result.price, 4)} €/kWh)`} value={eur(result.costeEnergia)}/>
            <Row label={`Impuesto eléctrico (${impElectPct}%)`} value={eur(result.impElectVal)}/>
            <Row label="Base IVA" value={eur(result.subtotal)}/>
            <Row label={`IVA (${ivaPct}%)`} value={eur(result.ivaVal)}/>
            <div className="border-t border-white/10 mt-2 pt-2">
              <Row label={<span className="font-black text-white">TOTAL / año</span>} value={<span className="font-black text-[#A3FF12]">{eur(result.total)}</span>}/>
            </div>
          </div>
        </div>
      </div>

      {/* ---- HISTORICO ---- */}
      <div className="bg-black/40 border border-white/10 rounded-2xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={18} className="text-[#A3FF12]"/>
          <h2 className="text-lg font-black">Histórico ({saved.length})</h2>
        </div>
        {saved.length === 0 ? (
          <div className="text-white/40 text-sm py-6 text-center">Sin cálculos guardados aún.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="historico-table">
              <thead className="text-white/50 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Fecha</th>
                  <th className="text-left px-3 py-2">Site</th>
                  <th className="text-right px-3 py-2">kWh/año</th>
                  <th className="text-right px-3 py-2">€/kWh</th>
                  <th className="text-right px-3 py-2">No consumida</th>
                  <th className="text-right px-3 py-2 text-[#A3FF12]">Total anual</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {saved.map((g) => (
                  <tr key={g.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-3 py-2 text-white/70 whitespace-nowrap">{new Date(g.created_at).toLocaleDateString('es-ES')}</td>
                    <td className="px-3 py-2">
                      {g.site_name ? <span className="text-white">{g.site_name}</span> : <span className="text-white/30">—</span>}
                      {g.site_code && <span className="text-white/40 text-xs ml-1">({g.site_code})</span>}
                      {g.notes && <div className="text-white/40 text-xs mt-0.5">{g.notes}</div>}
                    </td>
                    <td className="px-3 py-2 text-right text-white/70">{fmt(g.result?.kwh_anual_con_merma, 0)}</td>
                    <td className="px-3 py-2 text-right text-white/70">{fmt(g.price_eur_kwh, 4)}</td>
                    <td className="px-3 py-2 text-right text-white/50"><span className="inline-flex items-center gap-1"><Percent size={11}/>{fmt(g.merma_pct, 1)}</span></td>
                    <td className="px-3 py-2 text-right font-black text-[#A3FF12]">{eur(g.result?.total_anual)}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => del(g.id)} data-testid={`del-${g.id}`}
                        className="text-white/40 hover:text-red-400 p-1"><Trash2 size={14}/></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex justify-between items-center">
    <span className="text-white/60">{label}</span>
    <span className="text-white">{value}</span>
  </div>
);

export default GastosElectricos;
